/**
 * SupervisionMeetingFormPage
 *
 * Create, edit, sign, and review supervision meeting notes.
 *
 * Route params:
 *   :responseId — QR resource id (omitted for new notes)
 * Query params:
 *   type — 'supervision' | 'supervisor' | 'group-supervision' | 'group-supervisor'
 *   (only required when creating a new note; ignored when responseId is present)
 *
 * Role behavior:
 *   Provider (supervisee):
 *     - draft / edits-requested → editable form + Save Draft + Sign button
 *     - signed → read-only
 *     - cosigned → read-only
 *   Supervisor:
 *     - Their own supervisor notes (type=clinical-supervisor) → editable form
 *     - Supervisee notes (type=clinical-supervision) → read-only via DocumentReviewPage
 *       (supervisor reaches those via the Supervision Signing section → review/:id)
 *
 * Sign/lock mechanics:
 *   - Supervisee signs → same PROVIDER_SIGNATURE_EXT_URL as progress notes (QR status: completed)
 *   - Supervisor cosigns → SUPERVISOR_SIGNATURE_EXT_URL → note is locked (read-only)
 *   - Supervisor request edits → removes provider sig, adds REQUESTED_EDITS markers
 *     (handled in DocumentReviewPage for supervisee notes)
 */

import { useMedplum, useQuestionnaireForm } from '@medplum/react-hooks';
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Grid,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { practitionerPreferredReference } from '../../lib/loadPractitionerPreferredLabels';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconCheck, IconSignature } from '@tabler/icons-react';
import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SignatureCaptureModal } from '../../components/SignatureCapture';
import { SignatureBlock } from '@/components/SignatureBlock';
import { usePermissions } from '../../contexts/PermissionContext';
import { useCurrentUserPractitioner } from '../../hooks/useCurrentUserPractitioner';
import { fetchQuestionnaireForResponseRef } from '../appointments/utils/fhirHelpers';
import { fhirCreateQR, fhirUpdateQR } from '../appointments/utils/fhirWrite';
import {
  buildQuestionnaireResponseItemsForSave,
  shapeResponseItemsForQuestionnaire,
} from '../appointments/utils/questionnaireResponseShape';
import type { SignatureInfo } from '../appointments/fhirNoteMapping';
import {
  getSignatureFromQR,
  getRequestedEditsFromQR,
  PROVIDER_SIGNATURE_EXT_URL,
  SUPERVISOR_SIGNATURE_EXT_URL,
  removeRequestedEditsFromQR,
  setSignatureOnQR,
} from '../appointments/fhirNoteMapping';
import {
  SUPERVISION_MEETING_TAG_SYSTEM,
  SUPERVISION_NOTE_TYPE_TAG_SYSTEM,
  SUPERVISION_MEETING_TEMPLATE_SLUGS,
} from './supervisionFhirConstants';
import { SupervisionStatusBadge } from './components/SupervisionStatusBadge';
import { QRDocumentWithComments } from './components/QRDocumentWithComments';
import { PreviewItemArray } from '../settings/template-library/components/TemplatePreviewDrawer';
import { scrollToAndFlashQuestionnaireItem } from '../appointments/utils/scrollToQuestionnaireItem';
import { validateSupervisionMeetingBeforeSign } from './utils/supervisionMeetingFormValidation';
import { useVariableContext } from '../../lib/template-variables';
import { getTemplateCategory } from '../settings/template-library/utils/templateTags';

type NoteTypeParam = 'supervision' | 'supervisor' | 'group-supervision' | 'group-supervisor';

const TEMPLATE_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/questionnaire-template';

function resolveTemplateSlug(typeParam: NoteTypeParam): string {
  switch (typeParam) {
    case 'supervisor':
      return SUPERVISION_MEETING_TEMPLATE_SLUGS.supervisorNote;
    case 'group-supervisor':
      return SUPERVISION_MEETING_TEMPLATE_SLUGS.groupSupervisorNote;
    case 'group-supervision':
      return SUPERVISION_MEETING_TEMPLATE_SLUGS.groupSuperviseeNote;
    default:
      return SUPERVISION_MEETING_TEMPLATE_SLUGS.superviseeNote;
  }
}

function isSupervisorNoteType(type: NoteTypeParam): boolean {
  return type === 'supervisor' || type === 'group-supervisor';
}

function resolveNoteTypeFromQuestionnaire(
  questionnaireName: string | undefined,
  noteTypeTagCode: string | undefined,
): NoteTypeParam {
  if (questionnaireName?.includes('group-supervisor')) return 'group-supervisor';
  if (questionnaireName?.includes('group-supervision')) return 'group-supervision';
  if (noteTypeTagCode === 'clinical-supervisor') return 'supervisor';
  return 'supervision';
}

function getSupervisionMeetingPageTitle(type: NoteTypeParam): string {
  switch (type) {
    case 'supervisor':
      return 'Clinical Supervisor Note';
    case 'group-supervisor':
      return 'Group Clinical Supervisor Note';
    case 'group-supervision':
      return 'Group Clinical Supervision Note';
    default:
      return 'Clinical Supervision Note';
  }
}

async function resolveQuestionnaireBySlug(
  medplum: ReturnType<typeof useMedplum>,
  slug: string,
): Promise<Questionnaire | null> {
  // Try by name (most reliable)
  try {
    const byName = await medplum.searchResources('Questionnaire', { name: slug, _count: '5' });
    const match = byName.find((q) => q.name === slug) ?? byName[0];
    if (match) return match;
  } catch {
    // fall through
  }
  // Try by identifier
  try {
    const byId = await medplum.searchResources('Questionnaire', {
      identifier: `${TEMPLATE_IDENTIFIER_SYSTEM}|${slug}`,
      _count: '1',
    });
    if (byId[0]) return byId[0];
  } catch {
    // fall through
  }
  return null;
}

function useSupervisionMeetingFormData(
  responseId: string | undefined,
  typeParam: NoteTypeParam | null,
  templateId: string | null,
) {
  const medplum = useMedplum();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [savedResponse, setSavedResponse] = useState<QuestionnaireResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const doLoad = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (responseId) {
        // Editing/viewing an existing QR
        const qr = await medplum.readResource('QuestionnaireResponse', responseId);
        setSavedResponse(qr);
        if (qr.questionnaire) {
          const q = await fetchQuestionnaireForResponseRef(medplum, qr.questionnaire);
          setQuestionnaire(q);
        }
      } else {
        // New note — use the Administrative template selected on the Meetings tab.
        const slug = resolveTemplateSlug(typeParam ?? 'supervision');
        const q = templateId
          ? await medplum.readResource('Questionnaire', templateId)
          : await resolveQuestionnaireBySlug(medplum, slug);
        if (q && getTemplateCategory(q) !== 'administrative') {
          setError('The selected template is not in the Administrative category.');
          setQuestionnaire(null);
          setSavedResponse(null);
          return;
        }
        if (!q) {
          setError(
            `Template "${slug}" not found. Ensure document templates have been seeded ` +
              `(run npm run templates:documents:setup in medplum-server).`,
          );
        }
        setQuestionnaire(q);
        setSavedResponse(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load supervision meeting note');
    } finally {
      setLoading(false);
    }
  }, [medplum, responseId, typeParam, templateId]);

  useEffect(() => {
    doLoad();
  }, [doLoad]);

  return { questionnaire, savedResponse, setSavedResponse, loading, error, refetch: doLoad };
}

/** Inline editable form section, same pattern as ChartDocumentPage. */
function MeetingFormSection({
  questionnaire,
  savedResponse,
  isEditable,
  onLiveResponseChange,
  formResponseRef,
}: {
  questionnaire: Questionnaire;
  savedResponse: QuestionnaireResponse | null;
  isEditable: boolean;
  onLiveResponseChange: (r: QuestionnaireResponse | undefined) => void;
  formResponseRef: MutableRefObject<QuestionnaireResponse | undefined>;
}): JSX.Element {
  const { context: varCtx } = useVariableContext();

  const formDefaultValue = useMemo(() => {
    if (!savedResponse) return undefined;
    return {
      ...savedResponse,
      item:
        shapeResponseItemsForQuestionnaire(savedResponse.item, questionnaire) ?? savedResponse.item,
    };
  }, [questionnaire, savedResponse]);

  const formState = useQuestionnaireForm({
    questionnaire,
    defaultValue: formDefaultValue,
    disablePagination: false,
  });

  useEffect(() => {
    if ('loading' in formState && formState.loading) {
      onLiveResponseChange(savedResponse ?? undefined);
      return;
    }
    if ('questionnaireResponse' in formState) {
      formResponseRef.current = formState.questionnaireResponse;
      onLiveResponseChange(formState.questionnaireResponse);
    }
  }, [formState, formResponseRef, onLiveResponseChange, savedResponse]);

  if ('loading' in formState && formState.loading) {
    return (
      <Group gap="sm">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Loading form…
        </Text>
      </Group>
    );
  }

  return (
    <PreviewItemArray
      formState={formState}
      context={[]}
      items={formState.items}
      responseItems={formState.responseItems}
      questionnaireItems={questionnaire.item}
      variableContext={varCtx}
      readOnlyLinkIds={new Set()}
      readOnly={!isEditable}
    />
  );
}

function formatSignatureDateTime(date: Date): string {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function SupervisionMeetingFormContent(): JSX.Element {
  const { responseId } = useParams<{ responseId: string }>();
  const [searchParams] = useSearchParams();
  const typeParam = (searchParams.get('type') as NoteTypeParam | null) ?? 'supervision';
  const templateId = searchParams.get('template');
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { role } = usePermissions();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();

  const { questionnaire, savedResponse, setSavedResponse, loading, error } =
    useSupervisionMeetingFormData(responseId, typeParam, templateId);

  const [liveResponse, setLiveResponse] = useState<QuestionnaireResponse | undefined>(undefined);
  const formResponseRef = useRef<QuestionnaireResponse | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [signModalOpened, { open: openSignModal, close: closeSignModal }] = useDisclosure(false);

  const resolveLiveResponse = useCallback(
    (): QuestionnaireResponse | undefined => formResponseRef.current ?? liveResponse,
    [liveResponse],
  );

  const runSignValidation = useCallback((): boolean => {
    if (!questionnaire) return false;
    const response = resolveLiveResponse();
    const validation = validateSupervisionMeetingBeforeSign({
      questionnaire,
      response,
      practitioner,
    });
    if (validation.success) return true;
    if (validation.missingItemPath) {
      scrollToAndFlashQuestionnaireItem(validation.missingItemPath);
    }
    notifications.show({
      color: 'red',
      title: validation.missingLinkIds.length > 0 ? 'Missing required fields' : 'Cannot sign yet',
      message: validation.message,
    });
    return false;
  }, [questionnaire, practitioner, resolveLiveResponse]);

  const handleOpenSignModal = useCallback(() => {
    if (practitionerLoading) {
      notifications.show({
        color: 'yellow',
        title: 'Loading profile',
        message: 'Please wait for your profile to finish loading before signing.',
      });
      return;
    }
    if (!runSignValidation()) return;
    openSignModal();
  }, [openSignModal, practitionerLoading, runSignValidation]);

  const isNewNote = !responseId;
  const isSupervisorRole = role === 'supervisor';
  const providerSignature = savedResponse
    ? getSignatureFromQR(savedResponse, PROVIDER_SIGNATURE_EXT_URL)
    : undefined;
  const supervisorSignature = savedResponse
    ? getSignatureFromQR(savedResponse, SUPERVISOR_SIGNATURE_EXT_URL)
    : undefined;
  const requestedEdits = savedResponse ? getRequestedEditsFromQR(savedResponse) : false;

  const isCosigned = !!supervisorSignature;
  const isProviderSigned = !!providerSignature && !requestedEdits;

  // Determine the note type from the existing QR tag or the URL param.
  const resolvedNoteType: NoteTypeParam = useMemo(() => {
    if (savedResponse) {
      const tag = savedResponse.meta?.tag?.find((t) => t.system === SUPERVISION_NOTE_TYPE_TAG_SYSTEM);
      return resolveNoteTypeFromQuestionnaire(questionnaire?.name, tag?.code);
    }
    return typeParam;
  }, [savedResponse, questionnaire?.name, typeParam]);

  // A supervisor note locks after the supervisor (author) signs — no cosign from a second party.
  const isSupervisorNote = isSupervisorNoteType(resolvedNoteType);
  // Supervisor notes are locked after supervisor signature.
  const isLocked = isSupervisorNote ? isProviderSigned : isCosigned;

  // Who can edit: owner (author) before signing; supervisor edits blocked for supervisee notes.
  const currentPractitionerId = practitioner?.id;
  const authorId = savedResponse?.author
    ? savedResponse.author.reference?.replace('Practitioner/', '')
    : currentPractitionerId;
  const isAuthor = !savedResponse || currentPractitionerId === authorId;

  const canEdit = isAuthor && !isProviderSigned && !isCosigned;
  const showEditableForm = canEdit || isNewNote;

  // Sign label depends on note type and role
  const signButtonLabel = isSupervisorNote
    ? 'Sign and Lock'
    : 'Sign and Submit for Review';

  const backHref = '/dashboard/supervision/meetings';
  const pageTitle = getSupervisionMeetingPageTitle(resolvedNoteType);

  const handleSaveDraft = useCallback(async () => {
    const currentResponse = formResponseRef.current ?? liveResponse;
    if (!questionnaire || !currentResponse) {
      notifications.show({
        color: 'red',
        title: 'Cannot save',
        message: 'Form data is not ready yet. Please wait a moment and try again.',
      });
      return;
    }
    setSaving(true);
    try {
      const items = buildQuestionnaireResponseItemsForSave(
        questionnaire,
        currentResponse.item,
        savedResponse?.item,
      );

      const noteTypeCode = isSupervisorNote ? 'clinical-supervisor' : 'clinical-supervision';
      const meetingTags = [
        { system: SUPERVISION_MEETING_TAG_SYSTEM, code: 'true' },
        { system: SUPERVISION_NOTE_TYPE_TAG_SYSTEM, code: noteTypeCode },
      ];

      if (!savedResponse) {
        // Create new QR
        const newQR: QuestionnaireResponse = {
          resourceType: 'QuestionnaireResponse',
          status: 'in-progress',
          questionnaire: questionnaire.url ?? `Questionnaire/${questionnaire.id}`,
          author: practitioner ? practitionerPreferredReference(practitioner) : undefined,
          authored: new Date().toISOString(),
          item: items,
          meta: { tag: meetingTags },
        };
        const created = await fhirCreateQR(medplum, newQR);
        medplum.invalidateSearches('QuestionnaireResponse');
        setSavedResponse(created);
        // Replace URL so the page now tracks the created QR
        navigate(`/dashboard/supervision/meetings/${created.id}`, { replace: true });
      } else {
        const updated: QuestionnaireResponse & { id: string } = {
          ...savedResponse,
          id: savedResponse.id!,
          status: 'in-progress',
          item: items,
          authored: savedResponse.authored ?? new Date().toISOString(),
        };
        const result = await fhirUpdateQR(medplum, updated);
        medplum.invalidateSearches('QuestionnaireResponse');
        setSavedResponse(result);
      }

      notifications.show({
        message: 'Note saved',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (e) {
      notifications.show({
        title: 'Save failed',
        message: e instanceof Error ? e.message : 'Unknown error',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }, [questionnaire, liveResponse, savedResponse, isSupervisorNote, practitioner, medplum, navigate]);

  const handleSign = useCallback(
    async (signature: SignatureInfo) => {
      if (!questionnaire) return;
      if (!runSignValidation()) {
        closeSignModal();
        return;
      }
      const currentResponse = resolveLiveResponse();
      if (!currentResponse) {
        closeSignModal();
        notifications.show({
          color: 'red',
          title: 'Cannot sign',
          message: 'Form data is not ready. Please wait a moment and try again.',
        });
        return;
      }
      closeSignModal();
      setSaving(true);
      try {
        const items = buildQuestionnaireResponseItemsForSave(
          questionnaire,
          currentResponse.item,
          savedResponse?.item,
        );
        const noteTypeCode = isSupervisorNote ? 'clinical-supervisor' : 'clinical-supervision';
        const meetingTags = [
          { system: SUPERVISION_MEETING_TAG_SYSTEM, code: 'true' },
          { system: SUPERVISION_NOTE_TYPE_TAG_SYSTEM, code: noteTypeCode },
        ];

        let qr: QuestionnaireResponse;
        if (!savedResponse) {
          const newQR: QuestionnaireResponse = {
            resourceType: 'QuestionnaireResponse',
            status: 'in-progress',
            questionnaire: questionnaire.url ?? `Questionnaire/${questionnaire.id}`,
            author: practitioner ? practitionerPreferredReference(practitioner) : undefined,
            authored: new Date().toISOString(),
            item: items,
            meta: { tag: meetingTags },
          };
          qr = await fhirCreateQR(medplum, newQR);
        } else {
          qr = {
            ...savedResponse,
            id: savedResponse.id!,
            item: items,
            authored: savedResponse.authored ?? new Date().toISOString(),
          };
        }

        // Apply provider signature and set status to completed
        let signed = setSignatureOnQR(qr, PROVIDER_SIGNATURE_EXT_URL, signature);
        signed = removeRequestedEditsFromQR(signed);
        const finalQR: QuestionnaireResponse & { id?: string } = {
          ...signed,
          status: 'completed',
        };

        const result = finalQR.id
          ? await fhirUpdateQR(medplum, finalQR as QuestionnaireResponse & { id: string })
          : await fhirCreateQR(medplum, finalQR);

        medplum.invalidateSearches('QuestionnaireResponse');
        setSavedResponse(result);
        if (!savedResponse) {
          navigate(`/dashboard/supervision/meetings/${result.id}`, { replace: true });
        }

        const message = isSupervisorNote
          ? 'Supervisor note signed and locked.'
          : 'Supervision note signed. Your supervisor has been notified for review.';
        notifications.show({ message, color: 'green', icon: <IconCheck size={16} /> });
      } catch (e) {
        notifications.show({
          title: 'Sign failed',
          message: e instanceof Error ? e.message : 'Unknown error',
          color: 'red',
        });
      } finally {
        setSaving(false);
      }
    },
    [
      questionnaire,
      resolveLiveResponse,
      runSignValidation,
      savedResponse,
      isSupervisorNote,
      practitioner,
      medplum,
      navigate,
      closeSignModal,
    ],
  );

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Group gap="sm">
          <Loader size="sm" />
          <Text>Loading…</Text>
        </Group>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Could not load note">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!questionnaire) {
    return (
      <Container size="md" py="xl">
        <Alert color="orange" title="Template not found">
          The supervision note template could not be loaded. Please contact your administrator.
        </Alert>
      </Container>
    );
  }

  const statusBadge = savedResponse ? (
    <SupervisionStatusBadge
      status={
        isCosigned
          ? 'cosigned'
          : isProviderSigned
            ? 'signed'
            : requestedEdits
              ? 'edits-requested'
              : 'draft'
      }
      size="md"
    />
  ) : (
    <Badge variant="light" color="gray">
      New Draft
    </Badge>
  );

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Stack gap="xs">
          <Text
            component={Link}
            to={backHref}
            size="sm"
            c="dimmed"
            style={{ display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
          >
            <IconArrowLeft size={14} />
            Back to Supervision Meetings
          </Text>
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Title order={3}>{pageTitle}</Title>
              {savedResponse?.authored && (
                <Text size="sm" c="dimmed">
                  {formatSignatureDateTime(new Date(savedResponse.authored))}
                </Text>
              )}
            </Stack>
            {statusBadge}
          </Group>
        </Stack>

        {/* Edits requested banner (supervisee view) */}
        {requestedEdits && !isSupervisorRole && (
          <Alert color="orange" title="Edits Requested">
            Your supervisor has requested changes. Please revise and re-sign.
          </Alert>
        )}

        {/* Note form / read-only display */}
        <Paper p="xl" withBorder>
          {showEditableForm ? (
            <MeetingFormSection
              questionnaire={questionnaire}
              savedResponse={savedResponse}
              isEditable={true}
              onLiveResponseChange={setLiveResponse}
              formResponseRef={formResponseRef}
            />
          ) : (
            <Box>
              <QRDocumentWithComments
                questionnaireResponse={savedResponse!}
                questionnaire={questionnaire}
                comments={[]}
                onAddComment={() => {}}
                isSigned={isLocked}
                readOnly
                hideTitle
              />
            </Box>
          )}
        </Paper>

        {/* Signatures */}
        {(providerSignature || supervisorSignature) && (
          <Grid>
            {providerSignature && (
              <Grid.Col span={{ base: 12, sm: supervisorSignature ? 6 : 12 }}>
                <SignatureBlock label="Provider" signature={providerSignature} />
              </Grid.Col>
            )}
            {supervisorSignature && (
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <SignatureBlock label="Supervisor" signature={supervisorSignature} />
              </Grid.Col>
            )}
          </Grid>
        )}

        {/* Action footer */}
        {!isLocked && (
          <Group justify="flex-end" gap="sm">
            {showEditableForm && (
              <Button variant="default" onClick={handleSaveDraft} loading={saving} disabled={saving}>
                Save Draft
              </Button>
            )}
            {showEditableForm && (
              <Button
                leftSection={<IconSignature size={16} />}
                onClick={handleOpenSignModal}
                loading={saving}
                disabled={saving || practitionerLoading}
              >
                {signButtonLabel}
              </Button>
            )}
          </Group>
        )}
      </Stack>

      {/* Signature capture modal */}
      <SignatureCaptureModal
        opened={signModalOpened}
        onClose={closeSignModal}
        onSign={handleSign}
        title={`Sign ${pageTitle}`}
        submitLabel={signButtonLabel}
      />
    </Container>
  );
}

export function SupervisionMeetingFormPage(): JSX.Element {
  return <SupervisionMeetingFormContent />;
}
